import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { CheckCircle2, Circle, Loader, AlertCircle, LogOut } from 'lucide-react'
import GoogleConnectButton from './registration/GoogleConnectButton'
import { saveGoogleCredentials } from '../services/registrationApi'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

interface OnboardingLocation {
  id: string
  name: string
  has_google_credentials: boolean
}

interface OnboardingData {
  full_name: string
  business_name: string
  locations: OnboardingLocation[]
}

const STEPS = [
  { label: 'Create your account' },
  { label: 'Set up your business profile' },
  { label: 'Connect Google Business Profile' },
  { label: 'Fetch your first reviews' },
]

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<OnboardingData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    const jwt = localStorage.getItem('jwt_token')
    if (!jwt) {
      navigate('/login')
      return
    }

    fetch(`${BASE_URL}/api/dashboard`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(async (res) => {
        if (res.status === 401) {
          localStorage.removeItem('jwt_token')
          navigate('/login')
          return
        }
        if (!res.ok) throw new Error('Failed to load')
        const json: OnboardingData = await res.json()
        if (json.locations.every((l) => l.has_google_credentials)) {
          navigate('/dashboard')
          return
        }
        setData(json)
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setIsLoading(false))
  }, [navigate])

  const handleLogout = () => {
    localStorage.removeItem('jwt_token')
    navigate('/login')
  }

  const handleConnect = async (locationId: string, encryptedCredentials: string) => {
    setSaveError(null)
    try {
      await saveGoogleCredentials(locationId, encryptedCredentials)
      navigate('/dashboard')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save credentials.')
    }
  }

  const nav = (
    <nav className="relative z-10 px-6 py-4 border-b border-white/[0.07] flex items-center justify-between">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 no-underline hover:opacity-80 transition-opacity"
      >
        <img
          src={`${import.meta.env.BASE_URL}logo-icon.png`}
          alt="ReputeDesk"
          className="w-8 h-8 object-contain"
        />
        <span className="font-sora font-bold text-lg text-rd-text">ReputeDesk</span>
      </Link>
      <button
        type="button"
        onClick={handleLogout}
        className="inline-flex items-center gap-2 text-sm text-rd-muted hover:text-rd-text transition-colors"
      >
        <LogOut className="w-4 h-4" />
        Log out
      </button>
    </nav>
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-rd-bg font-dmSans">
        {nav}
        <div className="flex items-center justify-center min-h-64" role="status" aria-live="polite">
          <Loader className="w-6 h-6 animate-spin text-rd-accent" aria-hidden="true" />
          <span className="ml-3 text-rd-muted text-sm">Loading…</span>
        </div>
      </div>
    )
  }

  if (!data) return null

  const targetLocation = data.locations.find((l) => !l.has_google_credentials)
  if (!targetLocation) return null

  return (
    <div className="min-h-screen bg-rd-bg font-dmSans relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
        style={{ background: 'radial-gradient(ellipse, rgba(74,222,128,0.06) 0%, transparent 70%)' }}
      />

      {nav}

      <main className="relative z-10 max-w-xl mx-auto px-4 py-12">
        {/* Welcome heading */}
        <div className="mb-8">
          <h1 className="font-sora font-bold text-2xl text-rd-text">
            Welcome, {data.full_name.split(' ')[0] || data.business_name}!
          </h1>
          <p className="text-rd-muted text-sm mt-1">
            One quick step remaining to start fetching reviews automatically.
          </p>
        </div>

        {/* Progress checklist */}
        <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 space-y-4 mb-5">
          {STEPS.map((step, i) => {
            const done = i < 2
            const active = i === 2
            return (
              <div key={i} className="flex items-center gap-3">
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-rd-accent shrink-0" aria-hidden="true" />
                ) : active ? (
                  <div
                    className="w-5 h-5 rounded-full border-2 border-rd-accent shrink-0 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <div className="w-2 h-2 rounded-full bg-rd-accent" />
                  </div>
                ) : (
                  <Circle className="w-5 h-5 text-white/20 shrink-0" aria-hidden="true" />
                )}
                <span
                  className={`text-sm ${
                    done
                      ? 'text-rd-dim line-through'
                      : active
                        ? 'text-rd-text font-medium'
                        : 'text-white/20'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            )
          })}
        </div>

        {/* Connect card */}
        <div className="bg-rd-surface border border-rd-accent/20 rounded-2xl p-6 space-y-5">
          <div>
            <h2 className="font-sora font-semibold text-rd-text">
              Connect Google Business Profile
            </h2>
            <p className="text-sm text-rd-muted mt-1">
              ReputeDesk reads your reviews and drafts AI responses automatically.
              Google is where your customers already review you.
            </p>
          </div>

          <GoogleConnectButton
            isConnected={false}
            onConnect={(encryptedCredentials) =>
              handleConnect(targetLocation.id, encryptedCredentials)
            }
          />

          {saveError && (
            <p className="text-sm text-danger-500 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
              {saveError}
            </p>
          )}

          <div className="pt-1 border-t border-white/[0.07]">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-sm text-rd-muted hover:text-rd-text transition-colors"
            >
              Skip for now →
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
